import { t } from '@lingui/core/macro';
import { useBlocker } from '@tanstack/react-router';
import * as React from 'react';
import type { JSX } from 'react';

/** Unsaved-changes guard: while any guard is active, a TanStack blocker asks before navigating or closing the tab. */

type UnsavedChangesGuardOptions = {
    /** The guard blocks navigation while this is true. */
    when: boolean;
    /** Dialog title. Defaults to a shared translated title. */
    title?: string;
    /** Dialog body. Defaults to a shared translated message. */
    description?: string;
    /** Label of the button that abandons the changes and navigates. */
    confirmLabel?: string;
    /** Label of the button that stays on the page. */
    cancelLabel?: string;
};

type GuardCopy = Pick<UnsavedChangesGuardOptions, 'title' | 'description' | 'confirmLabel' | 'cancelLabel'>;

type GuardRegistry = {
    /** Adds an active guard's copy; the returned cleanup removes it. */
    register: (copy: GuardCopy) => () => void;
};

const NavigationGuardContext = React.createContext<GuardRegistry>({
    // Without a provider the guard is inert, tab close included: both are the root's blocker.
    register: () => () => {},
});

type PendingNavigation = {
    copy: GuardCopy;
    /** `true` abandons the changes and navigates, `false` stays. Only the first call counts. */
    resolve: (proceed: boolean) => void;
};

const PendingNavigationContext = React.createContext<PendingNavigation | null>(null);

/** The blocked navigation awaiting the user's decision, for the dialog in `components/NavigationGuard`. */
export function usePendingNavigation(): PendingNavigation | null {
    return React.useContext(PendingNavigationContext);
}

/** Owns the guard registry and its blocker. Headless: mount it inside the router via `<NavigationGuardProvider>`. */
export function NavigationGuardRoot({ children }: { children?: React.ReactNode }): JSX.Element {
    // Insertion-ordered: the copy of the guard that registered or changed last is the dialog's.
    const guardsRef = React.useRef(new Set<GuardCopy>());
    // State, so the blocker is registered only while dirty: a registered blocker makes every push asynchronous.
    const [hasActiveGuard, setHasActiveGuard] = React.useState(false);
    const blockedCopyRef = React.useRef<GuardCopy>({});

    const registry = React.useMemo<GuardRegistry>(() => {
        const guards = guardsRef.current;
        return {
            register: (copy) => {
                guards.add(copy);
                setHasActiveGuard(true);
                return () => {
                    guards.delete(copy);
                    setHasActiveGuard(guards.size > 0);
                };
            },
        };
    }, []);

    // Stable, or `useBlocker` would re-register the blocker on every render.
    const shouldBlockFn = React.useCallback(() => {
        const copy = [...guardsRef.current].at(-1);
        // The last guard can be gone before the blocker is, e.g. FormContainer's post-save redirect.
        if (copy === undefined) {
            return false;
        }
        blockedCopyRef.current = copy;
        return true;
    }, []);
    // Read when the event fires, for the same gap: no warning once no guard blocks.
    const enableBeforeUnload = React.useCallback(() => guardsRef.current.size > 0, []);

    // On back/forward the address bar shows the target while the dialog is open; it steps back only on "Stay".
    // A second blocked navigation replaces the first; a replaced back/forward keeps its URL until the next one.
    // A write ignoring the blocker lands under the open dialog, and "Leave page" then performs the first navigation.
    // Redirects don't ask: one a route throws, or the 401 fallback's to the email form, which loses the changes.
    // Until a new route's chunk loads, the left page's guard still blocks, and "Stay" there leaves anyway.
    const blocker = useBlocker({
        shouldBlockFn,
        withResolver: true,
        // Browsers show their own tab-close warning and ignore custom copy.
        enableBeforeUnload,
        disabled: !hasActiveGuard,
    });

    const pending = React.useMemo<PendingNavigation | null>(() => {
        if (blocker.status !== 'blocked') {
            return null;
        }
        const { proceed, reset } = blocker;
        // `proceed` and `reset` settle the same promise, which ignores a second answer.
        return { copy: blockedCopyRef.current, resolve: (leave) => (leave ? proceed() : reset()) };
    }, [blocker]);

    return (
        <NavigationGuardContext.Provider value={registry}>
            <PendingNavigationContext.Provider value={pending}>{children}</PendingNavigationContext.Provider>
        </NavigationGuardContext.Provider>
    );
}

/** Blocks while `when` is true; a save or discard that navigates away passes `ignoreBlocker: true` to skip it. */
export function useUnsavedChangesGuard({
    when,
    title,
    description,
    confirmLabel,
    cancelLabel,
}: UnsavedChangesGuardOptions): void {
    const { register } = React.useContext(NavigationGuardContext);

    // Passive, not layout: a Suspense fallback above runs layout cleanups, which would disarm a hidden guard.
    React.useEffect(() => {
        if (!when) {
            return undefined;
        }
        // Unregistered in the passive flush, so a write from that commit's layout effects still asks.
        return register({ title, description, confirmLabel, cancelLabel });
    }, [register, when, title, description, confirmLabel, cancelLabel]);
}

/** Shared default body of the guard dialog; also used by the dialog in `components/NavigationGuard`. */
export function defaultGuardDescription(): string {
    return t`Your changes will be lost if you leave this page. Are you sure you want to leave?`;
}
